import { useEffect, useRef } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  conversationAtom,
  INITIAL_PAGE_CONST,
  setSearchQueryAtom,
  setPageAtom,
  setLimitAtom,
  setCurrentConversationAtom,
  fetchConversationsAtom,
  addOrUpdateConversationAtom,
  setConversationsAtom,
  resetNewMessageCountAtom,
  incrementNewMessageCountAtom,
  getNewMessageCountAtom,
} from "@/store/conversations";

export const useConversations = () => {
  const [state] = useAtom(conversationAtom);
  const { conversations } = useAtomValue(conversationAtom);
  const setSearchQuery = useSetAtom(setSearchQueryAtom);
  const setPage = useSetAtom(setPageAtom);
  const setLimit = useSetAtom(setLimitAtom);
  const setCurrentConversation = useSetAtom(setCurrentConversationAtom);
  const fetchConversations = useSetAtom(fetchConversationsAtom);
  const addOrUpdateConversation = useSetAtom(addOrUpdateConversationAtom);
  const setConversations = useSetAtom(setConversationsAtom);
  const incrementNewMessageCount = useSetAtom(incrementNewMessageCountAtom);
  const resetNewMessageCount = useSetAtom(resetNewMessageCountAtom);
  const newMessageCount = useAtomValue(getNewMessageCountAtom);

  const prevSearchQueryRef = useRef<string | null>(state.searchQuery);
  const prevPageRef = useRef<number>(state.page);
  const isInitialMountRef = useRef(true);

  // Fetch when search query or page changes
  useEffect(() => {
    // Skip on initial mount - let the page component handle initial fetch
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevSearchQueryRef.current = state.searchQuery;
      prevPageRef.current = state.page;
      return;
    }

    // Prevent fetching if already loading
    if (state.isLoading) {
      return;
    }

    const searchQueryChanged = prevSearchQueryRef.current !== state.searchQuery;
    const pageChanged = prevPageRef.current !== state.page;

    // If search query changed, reset page to initial and fetch
    if (searchQueryChanged) {
      prevSearchQueryRef.current = state.searchQuery;
      if (state.page !== INITIAL_PAGE_CONST) {
        setPage(INITIAL_PAGE_CONST);
        // Page change will trigger this effect again, so return here
        return;
      }
      // Fetch with the new search query
      fetchConversations();
      return;
    }

    // If page changed (pagination), fetch the new page
    if (pageChanged) {
      prevPageRef.current = state.page;
      fetchConversations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchQuery, state.page]);

  return {
    INITIAL_PAGE: INITIAL_PAGE_CONST,
    conversations: conversations,
    conversationsTotal: state.total,
    conversationsPage: state.page,
    conversationsLimit: state.limit,
    setConversationsPage: setPage,
    setConversationsLimit: setLimit,
    isLoading: state.isLoading,
    fetchConversations,
    addOrUpdateConversation,
    setCurrentConversation,
    currentConversation: state.currentConversation,
    searchQuery: state.searchQuery,
    setSearchQuery,
    setConversations,
    newMessageCount,
    incrementNewMessageCount,
    resetNewMessageCount,
  };
};


